import { useCallback, useRef, useState } from 'react';
import { FYMAPIError } from 'fym-common/src/generics/errors/FYMAPIError';
import { InternalErrorCode } from 'fym-common/src/errors/internalErrorCodes';
import { SerializedSmartLink } from 'fym-common/src/db/serializersTypes';
import {
  usePublishSmartLinkMutation,
  useUpdateSmartLinkMutation,
  useVerifySmartLinkSlugMutation,
} from '../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../redux/api/utils/isSuccessfulResult';
import { EditorSaveTracker } from '../saves/editorSaveTracker';

export type EditorPublishState =
  | { kind: 'idle' }
  | { kind: 'publishing' }
  | { kind: 'failed' }
  /** A change of the draft did not save, so nothing was published. */
  | { kind: 'saveFailed' }
  | { kind: 'slugTaken'; suggestion: string | null };

interface UseEditorPublishProps {
  smartLink: SerializedSmartLink;
  /** Told whether this was the first publish: the first one opens the published sheet, a later one a toast. */
  onPublished: (isFirstPublish: boolean) => void;
  /** The saves of the editor: a publish waits until every change that waits or runs is in the draft. */
  saves: EditorSaveTracker;
}

/** Lets the cache updates of the save answers run before the publish answer replaces the page. */
function nextTask(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, 0);
  });
}

/**
 * "Publish changes" in the top bar (design a4): the publishing state, the slug-taken alert with a
 * free address, and the other errors. A click on the free address saves it and publishes. Every
 * publish first sends the changes that wait and waits for the saves in flight; when one of them
 * fails, nothing is published.
 */
export function useEditorPublish({ smartLink, onPublished, saves }: UseEditorPublishProps) {
  const [state, setState] = useState<EditorPublishState>({ kind: 'idle' });
  const [publishSmartLink] = usePublishSmartLinkMutation();
  const [updateSmartLink] = useUpdateSmartLinkMutation();
  const [verifySlug] = useVerifySmartLinkSlugMutation();
  const { accountId, id: smartLinkId } = smartLink;
  // Read before the call: the publish answer replaces the cached page.
  const isFirstPublish = smartLink.publishedSmartLinkVersion === null;
  // The page of the newest answer: a save in the flush can change it before the PATCH below.
  const smartLinkRef = useRef(smartLink);
  smartLinkRef.current = smartLink;

  /** True when every change that waited or ran is in the draft. */
  const flushSaves = useCallback(async (): Promise<boolean> => {
    const { ok } = await saves.flushAll();
    if (!ok) {
      setState({ kind: 'saveFailed' });
      return false;
    }
    await nextTask();
    return true;
  }, [saves]);

  const findSuggestion = useCallback(async (): Promise<string | null> => {
    try {
      const result = await verifySlug({
        accountId,
        smartLinkId,
        slug: smartLink.recentSmartLinkVersion.slug,
      }).unwrap();
      return result.availableSlug === '' ? null : result.availableSlug;
    } catch {
      return null;
    }
  }, [accountId, smartLink.recentSmartLinkVersion.slug, smartLinkId, verifySlug]);

  const runPublish = useCallback(async (): Promise<void> => {
    // A block with a wrong field is not saved; a publish would show its old value as "up to date".
    if (saves.getSnapshot().invalidCount > 0) {
      return;
    }
    setState({ kind: 'publishing' });
    if (!(await flushSaves())) {
      return;
    }
    const result = await publishSmartLink({ accountId, smartLinkId });
    if (isSuccessfulResult(result)) {
      setState({ kind: 'idle' });
      onPublished(isFirstPublish);
      return;
    }
    const { internalCode } = result.error as FYMAPIError;
    if (internalCode === InternalErrorCode.SmartLinkSlugIsTaken) {
      setState({ kind: 'slugTaken', suggestion: await findSuggestion() });
      return;
    }
    setState({ kind: 'failed' });
  }, [accountId, findSuggestion, flushSaves, isFirstPublish, onPublished, publishSmartLink, saves, smartLinkId]);

  const publish = useCallback(() => {
    runPublish().catch(() => setState({ kind: 'failed' }));
  }, [runPublish]);

  const applySuggestion = useCallback(
    (slug: string) => {
      const run = async (): Promise<void> => {
        setState({ kind: 'publishing' });
        // A name or theme that waits saves first: the PATCH below must carry it.
        if (!(await flushSaves())) {
          return;
        }
        // The PATCH replaces the whole version: name, theme and background ride along.
        const { name, theme, backgroundFile } = smartLinkRef.current.recentSmartLinkVersion;
        const saved = await updateSmartLink({
          accountId,
          smartLinkId,
          name,
          slug,
          theme,
          backgroundFileId: backgroundFile?.id,
        });
        if (!isSuccessfulResult(saved)) {
          setState({ kind: 'failed' });
          return;
        }
        await runPublish();
      };
      run().catch(() => setState({ kind: 'failed' }));
    },
    [accountId, flushSaves, runPublish, smartLinkId, updateSmartLink]
  );

  const dismiss = useCallback(() => setState({ kind: 'idle' }), []);

  return { state, publish, applySuggestion, dismiss };
}

export type EditorPublish = ReturnType<typeof useEditorPublish>;
